import {
  geoOrthographic,
  geoPath,
  geoContains,
  geoBounds,
  geoArea,
} from "d3-geo";
import { feature as topoFeature } from "topojson-client";
import type { GeoPermissibleObjects, GeoProjection } from "d3-geo";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import type { Topology, GeometryCollection } from "topojson-specification";
import {
  resolveIso,
  type RawCountryProperties,
} from "../data/iso";

const GEOMETRY_URL = `${import.meta.env.BASE_URL}data/countries-10m.json`;

type CountryFeature = Feature<Geometry, RawCountryProperties>;

const OCEAN = "#0c1422";
const LAND = "#2e3440";
const LAND_STROKE = "#0c1422";
const HOVER_FILL = "#4f8cff";
const HOVER_STROKE = "#88c0d0";
const TARGET_FILL = "#f0b657";
const TARGET_STROKE = "#ffd591";
const GUESS_FILL = "rgba(224, 116, 116, 0.35)";
const GUESS_STROKE = "#e07474";
// Period of the guess-marker flash, in milliseconds (one full alpha cycle).
const GUESS_FLASH_PERIOD_MS = 800;

const MIN_SCALE_FACTOR = 0.45;
const MAX_SCALE_FACTOR = 160;
// Bbox spans wider than this almost always come from a country with
// overseas territories (France includes French Guiana, USA includes Alaska
// + Hawaii). The mainland is what users recognize, so cap how far we
// "zoom out" for these.
const SPAN_CAP_DEG = 80;
// Heuristic constant: targetScale = SPAN_TARGET_DEG / span. Tuned so that
// a country with bbox span S° fills ~20% of the visible globe arc.
const SPAN_TARGET_DEG = 36;
const AUTO_ZOOM_MIN = 1;
// Lower bound on the span we'll feed into the auto-zoom heuristic. Without
// this, micronations (Vatican span ~0.005°, Monaco ~0.02°, San Marino
// ~0.1°) zoom to MAX_SCALE_FACTOR and the user sees only a tiny patch of
// land with no surrounding context. Treating any span below this as
// MIN_AUTO_SPAN_DEG keeps the camera pulled back enough to show neighbors.
const MIN_AUTO_SPAN_DEG = 4;
// When a target country projects to fewer pixels than this on either axis,
// also draw a circular pin marker at its centroid so it's actually visible
// (Vatican / Monaco / Nauru otherwise vanish at most zoom levels).
const TARGET_MARKER_MIN_PX = 14;
// Pin radius in CSS pixels. Big enough to spot, small enough to not bury
// the real geometry for slightly-larger targets.
const TARGET_MARKER_RADIUS_PX = 9;

interface GlobeState {
  rotation: [number, number, number];
  scaleFactor: number;
  hoveredIso: string | null;
  targetIso: string | null;
  guessIso: string | null;
  hoverEnabled: boolean;
  // ISO → fill color, used by the mastery heatmap view. When null, fall
  // back to the uniform LAND color.
  heatmap: Map<string, string> | null;
}

export interface FlyToTarget {
  rotation?: [number, number];
  bounds?: [[number, number], [number, number]];
  scaleFactor?: number;
  durationMs?: number;
}

export type CountryClickHandler = (iso: string | null) => void;

export interface GlobeController {
  flyTo: (target: FlyToTarget) => Promise<void>;
  flyToIso: (
    iso: string,
    opts?: { scaleFactor?: number; durationMs?: number },
  ) => Promise<void>;
  setTargetIso: (iso: string | null) => void;
  setGuessIso: (iso: string | null) => void;
  setHoverEnabled: (enabled: boolean) => void;
  setHeatmap: (map: Map<string, string> | null) => void;
  // Set a callback that fires when the user taps/clicks a country (tap =
  // pointerdown + pointerup at the same spot, no drag in between). Pass
  // null to disable. Used by Reverse mode in the arcade.
  setClickHandler: (handler: CountryClickHandler | null) => void;
  getHoveredIso: () => string | null;
  destroy: () => void;
}

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

const smoothstep = (t: number): number => t * t * (3 - 2 * t);

// shortest signed angular delta in degrees, in (-180, 180]
const shortestDelta = (from: number, to: number): number => {
  let d = ((to - from) % 360 + 540) % 360 - 180;
  if (d === -180) d = 180;
  return d;
};

const isOnVisibleHemisphere = (
  projection: GeoProjection,
  point: [number, number],
): boolean => {
  const projected = projection(point);
  return projected !== null && Number.isFinite(projected[0]);
};

export const mountWorldGlobe = async (
  container: HTMLElement,
): Promise<GlobeController> => {
  const canvas = document.createElement("canvas");
  canvas.style.display = "block";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.cursor = "grab";
  container.appendChild(canvas);

  const tooltip = document.createElement("div");
  tooltip.className = "globe-tooltip";
  tooltip.style.display = "none";
  container.appendChild(tooltip);

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");

  const response = await fetch(GEOMETRY_URL);
  if (!response.ok) {
    throw new Error(`failed to load ${GEOMETRY_URL}: ${response.status}`);
  }
  const topo = (await response.json()) as Topology<{
    countries: GeometryCollection<RawCountryProperties>;
  }>;
  const collection = topoFeature(
    topo,
    topo.objects.countries,
  ) as unknown as FeatureCollection<Geometry, RawCountryProperties>;
  const features = collection.features as CountryFeature[];
  // Build an ISO → feature map for hit-testing and flyToIso. Skip
  // dependencies / indeterminate / lease territories — they share their
  // parent country's ADM0_A3 or fall back to ISO_A3_EH (e.g. Baykonur
  // Cosmodrome's ISO_A3_EH is "KAZ", which would otherwise clobber
  // Kazakhstan). The metadata extractor applies the same filter so this
  // map stays in sync with countries.json.
  const featuresByIso = new Map<string, CountryFeature>();
  for (const f of features) {
    const type = (f.properties as { TYPE?: string }).TYPE;
    if (type === "Dependency" || type === "Indeterminate" || type === "Lease") {
      continue;
    }
    const iso = resolveIso(f.properties);
    if (iso) featuresByIso.set(iso, f);
  }

  // Hit-test order: smallest features first. Without this, hovering over
  // Vatican or San Marino would resolve to Italy (because Italy's polygon
  // also contains those coordinates and gets checked earlier in source
  // order). Pre-computed once; geoArea is in steradians and consistent
  // across the globe.
  const featuresByAreaAsc = [...featuresByIso.values()].sort(
    (a, b) => geoArea(a) - geoArea(b),
  );

  const state: GlobeState = {
    rotation: [0, -20, 0],
    scaleFactor: 1,
    hoveredIso: null,
    targetIso: null,
    guessIso: null,
    hoverEnabled: true,
    heatmap: null,
  };

  const projection = geoOrthographic().clipAngle(90).precision(0.3);
  const pathBuilder = geoPath(projection, ctx);

  let cssWidth = 0;
  let cssHeight = 0;
  let baseScale = 1;
  let dpr = 1;
  let renderPending = false;
  let rafHandle = 0;
  let destroyed = false;

  const computeBaseScale = (): number =>
    Math.min(cssWidth, cssHeight) * 0.46;

  const applyProjection = (): void => {
    projection
      .rotate(state.rotation)
      .scale(baseScale * state.scaleFactor)
      .translate([cssWidth / 2, cssHeight / 2]);
  };

  const draw = (): void => {
    if (cssWidth === 0 || cssHeight === 0) return;
    applyProjection();

    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    // Ocean (sphere fill)
    ctx.beginPath();
    pathBuilder({ type: "Sphere" } as GeoPermissibleObjects);
    ctx.fillStyle = OCEAN;
    ctx.fill();

    if (state.heatmap) {
      // Heatmap mode: each country gets its own color. Slower than the
      // uniform-fill path (one fill per feature), but only used on the
      // dedicated mastery view.
      const heatmap = state.heatmap;
      for (const f of features) {
        ctx.beginPath();
        pathBuilder(f);
        const iso = resolveIso(f.properties);
        ctx.fillStyle = (iso && heatmap.get(iso)) ?? LAND;
        ctx.fill();
        ctx.lineWidth = 0.4;
        ctx.strokeStyle = LAND_STROKE;
        ctx.stroke();
      }
    } else {
      // Uniform-fill fast path: one fill across all country polygons.
      ctx.beginPath();
      for (const f of features) pathBuilder(f);
      ctx.fillStyle = LAND;
      ctx.fill();
      ctx.lineWidth = 0.4;
      ctx.strokeStyle = LAND_STROKE;
      ctx.stroke();
    }

    // Target country (the session prompt's answer) — rendered first so the
    // hover highlight stacks on top of it when applicable.
    if (state.targetIso) {
      const target = featuresByIso.get(state.targetIso);
      if (target) {
        ctx.beginPath();
        pathBuilder(target);
        ctx.fillStyle = TARGET_FILL;
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = TARGET_STROKE;
        ctx.stroke();

        // If the projected polygon is too small to actually see (Vatican,
        // Monaco, Nauru…), drop a pin marker at the centroid so the user
        // can locate it. We use pathBuilder.bounds(), which returns the
        // pixel bbox after projection, so the test naturally accounts for
        // current zoom level.
        const [[bx0, by0], [bx1, by1]] = pathBuilder.bounds(target);
        const widthPx = bx1 - bx0;
        const heightPx = by1 - by0;
        if (
          Number.isFinite(widthPx) &&
          Number.isFinite(heightPx) &&
          (widthPx < TARGET_MARKER_MIN_PX || heightPx < TARGET_MARKER_MIN_PX)
        ) {
          const labelLng = target.properties.LABEL_X;
          const labelLat = target.properties.LABEL_Y;
          const point: [number, number] | null =
            typeof labelLng === "number" && typeof labelLat === "number"
              ? projection([labelLng, labelLat])
              : null;
          if (point && Number.isFinite(point[0]) && Number.isFinite(point[1])) {
            ctx.beginPath();
            ctx.arc(point[0], point[1], TARGET_MARKER_RADIUS_PX, 0, Math.PI * 2);
            ctx.fillStyle = TARGET_FILL;
            ctx.fill();
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = TARGET_STROKE;
            ctx.stroke();
          }
        }
      }
    }

    // The user's guess after a wrong answer — flashes red so they can see
    // where the country they typed actually is. Alpha pulses over time.
    if (state.guessIso && state.guessIso !== state.targetIso) {
      const guess = featuresByIso.get(state.guessIso);
      if (guess) {
        const phase =
          (performance.now() % GUESS_FLASH_PERIOD_MS) / GUESS_FLASH_PERIOD_MS;
        const pulse = 0.55 + 0.45 * Math.sin(phase * Math.PI * 2);
        ctx.save();
        ctx.globalAlpha = pulse;
        ctx.beginPath();
        pathBuilder(guess);
        ctx.fillStyle = GUESS_FILL;
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = GUESS_STROKE;
        ctx.stroke();
        ctx.restore();
      }
    }

    // Hovered country on top
    if (state.hoveredIso && state.hoveredIso !== state.targetIso) {
      const hovered = featuresByIso.get(state.hoveredIso);
      if (hovered) {
        ctx.beginPath();
        pathBuilder(hovered);
        ctx.fillStyle = HOVER_FILL;
        ctx.fill();
        ctx.lineWidth = 0.8;
        ctx.strokeStyle = HOVER_STROKE;
        ctx.stroke();
      }
    }

    // Sphere outline
    ctx.beginPath();
    pathBuilder({ type: "Sphere" } as GeoPermissibleObjects);
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = "#1a1f2a";
    ctx.stroke();

    ctx.restore();
  };

  const scheduleRender = (): void => {
    if (renderPending) return;
    renderPending = true;
    rafHandle = requestAnimationFrame(() => {
      renderPending = false;
      draw();
      // Re-arm while the guess marker is active so its alpha pulse animates.
      if (state.guessIso !== null && !destroyed) scheduleRender();
    });
  };

  const resize = (): void => {
    const rect = container.getBoundingClientRect();
    cssWidth = rect.width;
    cssHeight = rect.height;
    dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);
    baseScale = computeBaseScale();
    scheduleRender();
  };

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(container);
  resize();

  // Multi-pointer tracking. 1 active pointer = drag-to-rotate;
  // 2 active pointers = pinch-to-zoom. The set lets us cleanly handle the
  // transition between modes (e.g. dropping one finger mid-pinch returns
  // to drag mode without a jump).
  type ActivePointer = {
    x: number;
    y: number;
    // Where this pointer first went down — used to distinguish a tap (no
    // meaningful movement) from a drag.
    startX: number;
    startY: number;
    // Goes false the moment the pointer moves more than TAP_MOVE_THRESHOLD
    // pixels from start. Final pointerup only fires the click handler if
    // this is still true.
    isTap: boolean;
  };
  const pointers = new Map<number, ActivePointer>();
  let dragLastX = 0;
  let dragLastY = 0;
  let pinchStartDistance = 0;
  let pinchStartScale = 1;
  let clickHandler: CountryClickHandler | null = null;
  const TAP_MOVE_THRESHOLD = 6;

  const distanceBetween = (a: ActivePointer, b: ActivePointer): number => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.hypot(dx, dy);
  };

  const seedDragBaseline = (): void => {
    const next = pointers.values().next();
    if (next.done) return;
    dragLastX = next.value.x;
    dragLastY = next.value.y;
  };

  const seedPinchBaseline = (): void => {
    const iter = pointers.values();
    const p1 = iter.next().value;
    const p2 = iter.next().value;
    if (!p1 || !p2) return;
    pinchStartDistance = distanceBetween(p1, p2);
    pinchStartScale = state.scaleFactor;
  };

  const onPointerDown = (e: PointerEvent): void => {
    pointers.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
      startX: e.clientX,
      startY: e.clientY,
      isTap: true,
    });
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      // ignore — capture is best-effort
    }
    canvas.style.cursor = "grabbing";
    tooltip.style.display = "none";
    state.hoveredIso = null;
    if (pointers.size === 1) {
      seedDragBaseline();
    } else if (pointers.size === 2) {
      // A second finger landing means this is a pinch, not a tap.
      for (const p of pointers.values()) p.isTap = false;
      seedPinchBaseline();
    }
  };

  const onPointerMove = (e: PointerEvent): void => {
    const tracked = pointers.get(e.pointerId);
    if (tracked) {
      tracked.x = e.clientX;
      tracked.y = e.clientY;
      if (
        tracked.isTap &&
        Math.hypot(e.clientX - tracked.startX, e.clientY - tracked.startY) >
          TAP_MOVE_THRESHOLD
      ) {
        tracked.isTap = false;
      }

      if (pointers.size === 1) {
        // Single-finger drag — rotate the globe.
        const dx = e.clientX - dragLastX;
        const dy = e.clientY - dragLastY;
        dragLastX = e.clientX;
        dragLastY = e.clientY;

        const angularPerPixel = 90 / (baseScale * state.scaleFactor);
        const nextLambda = state.rotation[0] + dx * angularPerPixel;
        const nextPhi = clamp(
          state.rotation[1] - dy * angularPerPixel,
          -89,
          89,
        );
        state.rotation = [nextLambda, nextPhi, state.rotation[2]];
        scheduleRender();
      } else if (pointers.size >= 2) {
        // Two-finger pinch — zoom.
        const iter = pointers.values();
        const p1 = iter.next().value;
        const p2 = iter.next().value;
        if (p1 && p2 && pinchStartDistance > 0) {
          const dist = distanceBetween(p1, p2);
          const ratio = dist / pinchStartDistance;
          state.scaleFactor = clamp(
            pinchStartScale * ratio,
            MIN_SCALE_FACTOR,
            MAX_SCALE_FACTOR,
          );
          scheduleRender();
        }
      }
      return;
    }

    if (!state.hoverEnabled) {
      if (state.hoveredIso !== null) {
        state.hoveredIso = null;
        tooltip.style.display = "none";
        scheduleRender();
      }
      return;
    }

    // Hover detection (only when not dragging and hover is enabled)
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    applyProjection();

    // Cheap disk check first. The orthographic projection draws the globe
    // as a circle of radius `baseScale * scaleFactor` centred on the
    // canvas. Pixels outside that disk are off-globe — and d3.geoOrthographic.invert
    // returns degenerate coords for them (same fixed point regardless of
    // pixel position), which would otherwise pollute hit-testing.
    const dx = mx - cssWidth / 2;
    const dy = my - cssHeight / 2;
    const r = baseScale * state.scaleFactor;
    if (dx * dx + dy * dy > r * r) {
      if (state.hoveredIso !== null) {
        state.hoveredIso = null;
        tooltip.style.display = "none";
        scheduleRender();
      }
      return;
    }

    const inverted = projection.invert?.([mx, my]);
    if (!inverted || !isOnVisibleHemisphere(projection, inverted)) {
      if (state.hoveredIso !== null) {
        state.hoveredIso = null;
        tooltip.style.display = "none";
        scheduleRender();
      }
      return;
    }

    let hit: CountryFeature | null = null;
    // Iterate smallest-first so Vatican wins over Italy, San Marino over
    // Italy, Lesotho over South Africa, etc.
    for (const f of featuresByAreaAsc) {
      if (geoContains(f as GeoPermissibleObjects, inverted)) {
        hit = f;
        break;
      }
    }
    const iso = hit ? resolveIso(hit.properties) : null;
    if (iso !== state.hoveredIso) {
      state.hoveredIso = iso;
      scheduleRender();
    }
    if (hit) {
      const name =
        hit.properties.ADMIN ?? hit.properties.NAME ?? iso ?? "Unknown";
      tooltip.textContent = `${name} (${iso})`;
      tooltip.style.display = "block";
      tooltip.style.left = `${mx + 12}px`;
      tooltip.style.top = `${my + 12}px`;
    } else {
      tooltip.style.display = "none";
    }
  };

  // Resolve a screen-pixel to the country ISO underneath it, or null if
  // off-globe or over open ocean. Shared by hover and tap detection.
  const hitTestPixel = (mx: number, my: number): string | null => {
    applyProjection();
    const dx = mx - cssWidth / 2;
    const dy = my - cssHeight / 2;
    const r = baseScale * state.scaleFactor;
    if (dx * dx + dy * dy > r * r) return null;
    const inverted = projection.invert?.([mx, my]);
    if (!inverted || !isOnVisibleHemisphere(projection, inverted)) return null;
    for (const f of featuresByAreaAsc) {
      if (geoContains(f as GeoPermissibleObjects, inverted)) {
        return resolveIso(f.properties);
      }
    }
    return null;
  };

  const endPointer = (e: PointerEvent): void => {
    const tracked = pointers.get(e.pointerId);
    if (!tracked) return;
    const wasTap = tracked.isTap;
    pointers.delete(e.pointerId);
    try {
      canvas.releasePointerCapture(e.pointerId);
    } catch {
      // ignore — pointer may have already been released
    }
    if (pointers.size === 1) {
      // Pinch → drag: re-seed the drag baseline so the remaining finger's
      // motion doesn't snap to the canvas origin.
      seedDragBaseline();
    } else if (pointers.size === 0) {
      canvas.style.cursor = "grab";
    }
    const handler = clickHandler;
    if (wasTap && handler) {
      // Fire after capture release so any state changes the handler makes
      // (e.g. flyToIso) see a fresh pointer state.
      const rect = canvas.getBoundingClientRect();
      const iso = hitTestPixel(
        tracked.x - rect.left,
        tracked.y - rect.top,
      );
      handler(iso);
    }
  };

  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const factor = Math.exp(-e.deltaY * 0.0015);
    state.scaleFactor = clamp(
      state.scaleFactor * factor,
      MIN_SCALE_FACTOR,
      MAX_SCALE_FACTOR,
    );
    scheduleRender();
  };

  const onPointerLeave = (e: PointerEvent): void => {
    // Mouse-only: clear the hover state when the cursor leaves the canvas.
    // Don't touch the pointers map (the user might still be dragging).
    if (e.pointerType === "mouse" && pointers.size === 0) {
      state.hoveredIso = null;
      tooltip.style.display = "none";
      scheduleRender();
    }
  };

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", endPointer);
  canvas.addEventListener("pointercancel", endPointer);
  canvas.addEventListener("pointerleave", onPointerLeave);
  canvas.addEventListener("wheel", onWheel, { passive: false });

  const flyTo = (target: FlyToTarget): Promise<void> => {
    let targetRotation: [number, number];
    let targetScale = target.scaleFactor ?? state.scaleFactor;

    if (target.bounds) {
      const [[w, s], [e, n]] = target.bounds;
      let lng = (w + e) / 2;
      // handle bbox that wraps the antimeridian
      if (e < w) lng += 180;
      const lat = (s + n) / 2;
      targetRotation = [-lng, -lat];

      if (target.scaleFactor === undefined) {
        const lngSpan = Math.abs(e - w);
        const latSpan = Math.abs(n - s);
        const rawSpan = Math.max(lngSpan, latSpan);
        // Floor at MIN_AUTO_SPAN_DEG so micronations don't zoom past usable
        // context; cap at SPAN_CAP_DEG so countries with far overseas
        // territories don't zoom out to the whole hemisphere.
        const span = Math.min(
          SPAN_CAP_DEG,
          Math.max(rawSpan, MIN_AUTO_SPAN_DEG),
        );
        targetScale = clamp(
          SPAN_TARGET_DEG / span,
          AUTO_ZOOM_MIN,
          MAX_SCALE_FACTOR,
        );
      }
    } else if (target.rotation) {
      targetRotation = target.rotation;
    } else {
      targetRotation = [state.rotation[0], state.rotation[1]];
    }

    const fromLambda = state.rotation[0];
    const fromPhi = state.rotation[1];
    const fromScale = state.scaleFactor;
    const dLambda = shortestDelta(fromLambda, targetRotation[0]);
    const dPhi = targetRotation[1] - fromPhi;
    const dScale = targetScale - fromScale;
    const duration = target.durationMs ?? 900;

    return new Promise((resolve) => {
      const start = performance.now();
      const tick = (now: number): void => {
        if (destroyed) {
          resolve();
          return;
        }
        const t = clamp((now - start) / duration, 0, 1);
        const eased = smoothstep(t);
        state.rotation = [
          fromLambda + dLambda * eased,
          clamp(fromPhi + dPhi * eased, -89, 89),
          state.rotation[2],
        ];
        state.scaleFactor = fromScale + dScale * eased;
        scheduleRender();
        if (t < 1) requestAnimationFrame(tick);
        else resolve();
      };
      requestAnimationFrame(tick);
    });
  };

  const destroy = (): void => {
    destroyed = true;
    resizeObserver.disconnect();
    cancelAnimationFrame(rafHandle);
    canvas.removeEventListener("pointerdown", onPointerDown);
    canvas.removeEventListener("pointermove", onPointerMove);
    canvas.removeEventListener("pointerup", endPointer);
    canvas.removeEventListener("pointercancel", endPointer);
    canvas.removeEventListener("pointerleave", onPointerLeave);
    canvas.removeEventListener("wheel", onWheel);
    canvas.remove();
    tooltip.remove();
  };

  const flyToIso = (
    iso: string,
    opts: { scaleFactor?: number; durationMs?: number } = {},
  ): Promise<void> => {
    const feature = featuresByIso.get(iso);
    if (!feature) {
      console.warn(`[globe] flyToIso: no feature for iso ${iso}`);
      return Promise.resolve();
    }

    const [[w, s], [e, n]] = geoBounds(feature) as [
      [number, number],
      [number, number],
    ];
    const rawSpan = Math.max(Math.abs(e - w), Math.abs(n - s));
    const span = Math.min(
      SPAN_CAP_DEG,
      Math.max(rawSpan, MIN_AUTO_SPAN_DEG),
    );

    // Center on NE's curated LABEL_X/LABEL_Y when available — bbox centers
    // are wrong for countries with overseas territories (France's bbox
    // includes French Guiana, USA's includes Alaska + Hawaii etc).
    const labelLng = feature.properties.LABEL_X;
    const labelLat = feature.properties.LABEL_Y;
    const target: FlyToTarget = {};
    if (typeof labelLng === "number" && typeof labelLat === "number") {
      target.rotation = [-labelLng, -labelLat];
    } else {
      let lng = (w + e) / 2;
      if (e < w) lng += 180;
      target.rotation = [-lng, -(s + n) / 2];
    }
    target.scaleFactor =
      opts.scaleFactor ??
      clamp(SPAN_TARGET_DEG / span, AUTO_ZOOM_MIN, MAX_SCALE_FACTOR);
    if (opts.durationMs !== undefined) target.durationMs = opts.durationMs;
    return flyTo(target);
  };

  const setTargetIso = (iso: string | null): void => {
    if (state.targetIso === iso) return;
    state.targetIso = iso;
    scheduleRender();
  };

  const setGuessIso = (iso: string | null): void => {
    if (state.guessIso === iso) return;
    state.guessIso = iso;
    scheduleRender();
  };

  const setHeatmap = (map: Map<string, string> | null): void => {
    state.heatmap = map;
    scheduleRender();
  };

  const setClickHandler = (handler: CountryClickHandler | null): void => {
    clickHandler = handler;
  };

  const setHoverEnabled = (enabled: boolean): void => {
    if (state.hoverEnabled === enabled) return;
    state.hoverEnabled = enabled;
    if (!enabled) {
      state.hoveredIso = null;
      tooltip.style.display = "none";
      scheduleRender();
    }
  };

  return {
    flyTo,
    flyToIso,
    setTargetIso,
    setGuessIso,
    setHoverEnabled,
    setHeatmap,
    setClickHandler,
    getHoveredIso: () => state.hoveredIso,
    destroy,
  };
};
